import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { PasswordInput } from '../../components/ui/PasswordInput';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { sanitizeInput, isValidEmail, validatePassword } from '../../utils/security';
import { Mail, User as UserIcon, Phone, AlertCircle } from 'lucide-react';

/**
 * Client-side mirror of CONSTANTS.SECURITY_CONFIG_DEFAULTS so the registration
 * form can validate before round-tripping to the backend policy engine.
 */
const DEFAULT_PASSWORD_POLICY = {
  password_min_length: 4,
  password_require_uppercase: false,
  password_require_numbers: false,
  password_require_special: false,
};

interface RegisterPageProps {
  onNavigateLogin?: () => void;
}

export const RegisterPage: React.FC<RegisterPageProps> = ({ onNavigateLogin }) => {
  const { register, isLoading } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Full name is required.';
    if (!isValidEmail(email)) errors.email = 'Please enter a valid email address.';
    const policy = validatePassword(password, DEFAULT_PASSWORD_POLICY);
    if (!policy.valid) errors.password = policy.errors[0];
    if (password !== confirmPassword) errors.confirmPassword = 'Passwords do not match.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!validate()) return;

    const res = await register({
      name: sanitizeInput(name),
      email: sanitizeInput(email),
      password,
      phone: phone ? sanitizeInput(phone) : undefined,
    });

    if (!res.success) {
      setErrorMessage(res.message || 'Registration failed. Please try again.');
    }
  };

  return (
    <AuthLayout
      title="Create Account"
      subtitle="Register to raise and track maintenance complaints"
      footer={
        onNavigateLogin && (
          <span>
            Already have an account?{' '}
            <button type="button" onClick={onNavigateLogin} className="link-button">
              Sign in
            </button>
          </span>
        )
      }
    >
      <form onSubmit={handleSubmit} autoComplete="on" className="space-y-4">
        {errorMessage && (
          <div className="form-alert">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}

        <Input
          label="Full Name"
          id="name"
          name="name"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          icon={<UserIcon />}
          error={fieldErrors.name}
        />

        <Input
          label="Email Address"
          type="email"
          id="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@company.com"
          icon={<Mail />}
          error={fieldErrors.email}
        />

        <Input
          label="Phone (optional)"
          type="tel"
          id="phone"
          name="phone"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+92 300 0000000"
          icon={<Phone />}
        />

        <PasswordInput
          label="Password"
          id="password"
          name="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          error={fieldErrors.password}
          hint="Minimum 4 characters."
        />

        <PasswordInput
          label="Confirm Password"
          id="confirmPassword"
          name="confirmPassword"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          error={fieldErrors.confirmPassword}
        />

        <Button type="submit" variant="primary" className="w-full mt-2" isLoading={isLoading}>
          Create Account
        </Button>
      </form>
    </AuthLayout>
  );
};
